import { Grid2, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { DownloadButton } from '@components/DownloadButton';
import Icon from '@components/Icon/Icon';
import UploadRecordsContent from '@components/Modal/Content/UploadRecordsModalContent/UploadRecordsContent';
import RoundedButton from '@components/RoundedButton/index';
import { StatusPill } from '@components/StatusPill';

import { useGetDocuments } from '@hooks/api/document';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import { formatDate } from '@utils/format';

import type { DocumentModel, StatusModel } from 'generated/types';

import DocumentName from './DocumentName';

const messages = defineMessages({
  title: {
    id: 'uploadsMain.title',
    defaultMessage: 'Records/Documents',
  },
  button: {
    id: 'uploadsMain.button',
    defaultMessage: 'Upload Records',
  },
  noUploads: {
    id: 'uploadsMain.noUploads',
    defaultMessage: 'No Uploads',
  },
});

const UploadedRecordsList: React.FC<{
  /***
   * Is this the table shown on the client overview page?
   */
  curatorView?: boolean;
  /**
   * Is this the table shown on the discern dashboard?
   */
  discern?: boolean;
}> = ({ curatorView, discern }) => {
  const { formatMessage } = useIntl();
  const { closeModal, openModal, setContent } = useModal();
  const {
    state: { isVitall },
  } = useSession();

  const { data, loading } = useGetDocuments();

  const tableData = (data ?? []).map((document) => {
    const { created_at, document_id, name, status } = document;
    return {
      name,
      date: created_at,
      status,
      document_id,
      document,
    };
  });

  const handleOpenModal = () => {
    openModal({
      width: 500,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: <UploadRecordsContent onSubmit={closeModal} />,
    });
  };

  return (
    <ContentLoader loading={loading}>
      <MuiDataTable
        columns={[
          {
            name: 'document',
            className: 'small',
            options: {
              searchable: true,
              customHeadLabelRender: () => 'File Name',
              customBodyRender: (doc: DocumentModel) => {
                return <DocumentName doc={doc} />;
              },
            },
          },
          {
            name: 'date',
            className: 'date',
            label: 'Date Uploaded',
            options: {
              customBodyRender: (date: string) =>
                formatDate({ date, formatter: 'datetime' }),
              filter: true,
            },
          },
          {
            name: 'status',
            label: 'Status',
            className: 'small',
            options: {
              customBodyRender: (status: StatusModel) => (
                <StatusPill color={status.color} name={status.public_name} />
              ),
              filter: true,
              filterType: 'multiselect',
              display: isVitall,
            },
          },
          {
            name: 'document_id',
            options: {
              customHeadLabelRender: () => 'Actions',
              customBodyRender: (document_id: string) => (
                <Stack direction="row" spacing={2}>
                  <DownloadButton document_id={document_id} type="view" />
                  <DownloadButton document_id={document_id} type="download" />
                </Stack>
              ),
            },
          },
          {
            name: '',
            options: {
              customBodyRender: () => (
                <Icon
                  color="text.secondary"
                  name="brain"
                  size="lg"
                  type="far"
                />
              ),
              display: discern,
            },
          },
          {
            name: 'status',
            options: {
              customHeadLabelRender: () => 'Discern Status',
              customBodyRender: (status: StatusModel) => (
                <StatusPill color={status.color} name={status.public_name} />
              ),
              display: discern,
            },
          },
          {
            name: '',
            options: {
              customHeadLabelRender: () => '',
              customBodyRender: () => (
                <Icon name="chevron-right" size="lg" type="fad" />
              ),
              display: discern,
            },
          },
        ]}
        data={tableData}
        options={{
          sortOrder: {
            name: 'date',
            direction: 'desc',
          },
          sort: true,
          search: true,
          filter: true,
          textLabels: {
            body: { noMatch: formatMessage(messages.noUploads) },
          },
        }}
        title={
          <Grid2 sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
            <Typography sx={{ fontSize: '16px' }}>
              {formatMessage(messages.title)}
            </Typography>

            {(curatorView || discern) && (
              <RoundedButton
                fontSize={14}
                height="35px"
                onClick={handleOpenModal}
                width="180px"
              >
                {formatMessage(messages.button)}
              </RoundedButton>
            )}
          </Grid2>
        }
      />
    </ContentLoader>
  );
};

export default UploadedRecordsList;
